import React from "react";
import "./App.scss";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import "./configureAmplify";
import RootContainer from "./components/RootContainer";
import { SingletonHooksContainer } from "react-singleton-hook";
import useOffline from "./hooks/useOffline";
import OfflineError from "./components/ProjectTab/SyncProject/OfflineError/OfflineError";
import { useTheme } from "./hooks/useSingleton";
import { View, Image, Text } from "@aws-amplify/ui-react";
import { BrowserRouter } from "react-router";
import { Button } from "@aws-amplify/ui-react";

/**
 * This handles rendering the rootcontainer, there is an Authenticator set here, so if you use the provided
 * signOut method to sign out it should redirect the user back to this page.
 * Keep in mind we will have to add in the disconnect flow to wherever the signout is being called so there is
 * no potential data loss
 */
function App() {
	const {
		clearNotConnected,
		estimateReadable,
		loading,
		notConnected,
		online,
		persisted,
		quota,
		quotaReadable,
		setUserOffline,
		setUserOnline,
		usage,
		usageReadable,
		userErrorMessage,
	} = useOffline();

	const color = useTheme();
	const header = {
		branding: () => {
			return (
				<View display={"flex"} style={{ justifyContent: "center" }} paddingTop={"32px"}>
					<Image alt="Record logo" src="/logo192.png" width={"60px"} height={"60px"} />
					<Text color={color.text.tertiary} fontSize={18} alignSelf={"center"} fontWeight={900} fontFamily={'"Inter", "sans-serif"'}>
						FactorEarth
					</Text>
				</View>
			);
		},
	};
	const components = {
		SignIn: {
			Header: () => header.branding(),
			Footer() {
				const { toForgotPassword } = useAuthenticator();

				return (
					<View textAlign="center" paddingBottom={"32px"}>
						<Button fontWeight="bold" onClick={toForgotPassword} variation="link" color={color.text.primary}>
							Reset Password
						</Button>
					</View>
				);
			},
		},

		SignUp: {
			Header: () => header.branding(),
			Footer() {
				const { toSignIn } = useAuthenticator();

				return (
					<View textAlign="center">
						<Button fontWeight="bold" onClick={toSignIn} size="small" variation="link" color={color.text.primary}>
							Back to Sign In
						</Button>
					</View>
				);
			},
		},
	};

	return (
		<>
			<SingletonHooksContainer />
			{notConnected ? (
				<OfflineError cb={clearNotConnected} color={color} errorMessage={"You don't have internet connection, and could not go into online mode"} />
			) : loading ? (
				<div className="spinner">
					<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24">
						<path fill="#7e818d" d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity=".5" />
						<path
							fill="#7b69e0"
							d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z">
							<animateTransform attributeName="transform" dur="1.5s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12" />
						</path>
					</svg>
				</div>
			) : online ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						background: color.background.tertiary,
						height: "100dvh",
						flexGrow: 1,
						width: "100%",
						overflow: "hidden",
					}}>
					<Authenticator components={components} hideSignUp>
						{({ signOut, user }) => (
							<div
								style={{
									display: "flex",
									background: color.background.primary,
									justifyContent: "left",
									zIndex: 0,
									overflowY: "hidden",
									width: "100%",
								}}>
								<BrowserRouter>
									<RootContainer
										estimateReadable={estimateReadable}
										online={online}
										persisted={persisted}
										quota={quota}
										quotaReadable={quotaReadable}
										setUserOffline={setUserOffline}
										setUserOnline={setUserOnline}
										signOut={signOut}
										usage={usage}
										usageReadable={usageReadable}
										userErrorMessage={userErrorMessage}
									/>
								</BrowserRouter>
							</div>
						)}
					</Authenticator>
				</div>
			) : (
				<div
					style={{
						display: "flex",
						background: color.background.primary,
						height: "100dvh",
						flexGrow: 1,
						width: "100%",
						zIndex: 0,
						overflow: "hidden",
					}}>
					<BrowserRouter>
						<RootContainer
							estimateReadable={estimateReadable}
							online={online}
							persisted={persisted}
							quota={quota}
							quotaReadable={quotaReadable}
							setUserOffline={setUserOffline}
							setUserOnline={setUserOnline}
							signOut={() => console.log("Don't do that")}
							usage={usage}
							usageReadable={usageReadable}
							userErrorMessage={userErrorMessage}
						/>
					</BrowserRouter>
				</div>
			)}
		</>
	);
}

export default App;
